/**
 * The bottom nav's Customize preference — which destinations a person put
 * first, and which they hid — as pure data, so it can be tested without a DOM.
 *
 * The shape and the merge rule are the seller portal's
 * (`apps/seller/src/shared/layout/BottomNav.tsx`): a saved `order` is replayed
 * over the app's current list, a key the app no longer offers is dropped, and a
 * destination added since the preference was saved is appended in its default
 * place rather than lost. A permission change therefore never strands a row —
 * the list is always the app's list, only re-sorted and filtered.
 */
export type NavCustomize = { order: string[]; hidden: string[] };

export const EMPTY_NAV_CUSTOMIZE: NavCustomize = { order: [], hidden: [] };

/** Parse a stored preference. Anything unreadable is "no preference". */
export function parseNavCustomize(raw: string | null): NavCustomize {
  if (!raw) return EMPTY_NAV_CUSTOMIZE;
  try {
    const parsed = JSON.parse(raw) as Partial<NavCustomize> | null;
    if (!parsed || typeof parsed !== "object") return EMPTY_NAV_CUSTOMIZE;
    return {
      order: Array.isArray(parsed.order)
        ? parsed.order.filter((k): k is string => typeof k === "string")
        : [],
      hidden: Array.isArray(parsed.hidden)
        ? parsed.hidden.filter((k): k is string => typeof k === "string")
        : [],
    };
  } catch {
    return EMPTY_NAV_CUSTOMIZE;
  }
}

/** Applies the saved order (unknown keys appended in default order) + hidden set. */
export function applyNavCustomize<T extends { key: string }>(
  items: readonly T[],
  customize: NavCustomize,
): { ordered: T[]; visible: T[] } {
  const byKey = new Map(items.map((item) => [item.key, item]));
  const seen = new Set<string>();
  const ordered: T[] = [];
  for (const key of customize.order) {
    const item = byKey.get(key);
    if (item && !seen.has(key)) {
      ordered.push(item);
      seen.add(key);
    }
  }
  for (const item of items) {
    if (!seen.has(item.key)) {
      ordered.push(item);
      seen.add(item.key);
    }
  }
  const hidden = new Set(customize.hidden);
  return { ordered, visible: ordered.filter((item) => !hidden.has(item.key)) };
}

/** Swap a key with its neighbour in the full order. Out of range is a no-op. */
export function moveNavKey(keys: readonly string[], index: number, dir: -1 | 1): string[] {
  const next = [...keys];
  const to = index + dir;
  if (index < 0 || index >= next.length || to < 0 || to >= next.length) return next;
  [next[index], next[to]] = [next[to], next[index]];
  return next;
}
